import { parseCookie } from 'cookie';

import type { UserCookies } from './types';
import { readUuid } from './values';

/**
 * Segment's anonymous id, and the only identity cookie a browser can read.
 *
 * The Cloudflare Worker writes `odauth_aid` and `odauth_cuid` `httpOnly` so
 * ad-blockers cannot strip them, which also puts them out of reach of
 * `document.cookie`. It mirrors the same value into this cookie without
 * `httpOnly` because the Segment SDK needs it from JS, so this is the browser's
 * view of `odAuthAid`.
 */
const BROWSER_ANONYMOUS_ID_COOKIE = 'ajs_anonymous_id';

/**
 * Read the identity cookies available to this document.
 *
 * `odAuthCuid` is always null. `odauth_cuid` is `httpOnly` and has no
 * non-`httpOnly` mirror, so no browser can see it. Segment's `ajs_user_id` is
 * not a substitute: it holds whatever the app last passed to
 * `analytics.identify()`, which is client-asserted rather than server-set.
 * A customer id in the browser needs a deliberate mirror from the Worker.
 */
export const getBrowserUserCookies = (): UserCookies => {
  if (typeof document === 'undefined') {
    return { odAuthAid: null, odAuthCuid: null };
  }

  const cookies = parseCookie(document.cookie);
  return {
    odAuthAid: readUuid(cookies[BROWSER_ANONYMOUS_ID_COOKIE]),
    odAuthCuid: null,
  };
};
